// hooks/useRecentsData.js - SPECIALIZED HOOK FOR RECENTS DATA FETCHING
import { useState, useEffect, useCallback, useRef } from 'react';

const apiKey = process.env.REACT_APP_API_KEY
// Cache for API responses
const recentsCache = new Map();
const CACHE_DURATION = 3 * 60 * 1000; // 3 minutes for recents (shorter than regular feeds)

// Allow items dated slightly in the "future" to account for timezone offsets
// and clock skew between the API and the client. Anything beyond this window
// is treated as a bad/future-dated record and filtered out of the feed.
const FUTURE_DATE_TOLERANCE_MS = 12 * 60 * 60 * 1000; // 12 hours

// Country schema mapping for API calls
const COUNTRY_SCHEMA_MAPPING = {
  'us': 'united_states_of_america',
  'canada': 'canada',
  'mexico': 'mexico',
  'uk': 'united_kingdom'
};

/**
 * Remove feed items whose created_at is dated in the future (beyond the allowed
 * tolerance). Items with a missing or unparseable date are kept as-is so we
 * don't silently drop legitimate content that simply lacks a clean timestamp.
 *
 * Non-array inputs are passed through untouched so this can't change behavior
 * for any unexpected response shape.
 */
const dropFutureDated = (posts) => {
  if (!Array.isArray(posts)) return posts;
  const cutoff = Date.now() + FUTURE_DATE_TOLERANCE_MS;
  return posts.filter((post) => {
    const rawDate = post && post.created_at;
    if (!rawDate) return true; // no date -> keep
    const parsed = new Date(rawDate);
    if (isNaN(parsed.getTime())) return true; // unparseable -> keep
    return parsed.getTime() <= cutoff;
  });
};

export const useRecentsData = (countryCode, feedLength, searchQuery, searchPerformed) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const abortControllerRef = useRef(null);
  const lastFetchParams = useRef(null);

  // Get the correct schema for the API call
  const getApiSchema = useCallback((country) => {
    return COUNTRY_SCHEMA_MAPPING[country] || country;
  }, []);

// Build API URL for recents.
//
// Recents always loads the standard recents feed. Search is applied
// client-side over the fetched data via filterDataBySearch — the backend has
// no working search route for the `all_recents` aggregate (it 500s, since
// all_recents is a virtual aggregate, not a real table), so we never build a
// /search/ URL here. Because the URL no longer changes when searching, a
// search reuses the already-cached recents data and filters it in place with
// no extra network request.
const buildRecentsApiUrl = useCallback(() => {
  const baseUrl = 'https://api.govglance.org';
  const schema = getApiSchema(countryCode);

  // US uses the original endpoint structure
  if (countryCode === 'us') {
    return `${baseUrl}/posts/recent?limit=${feedLength}&skip=0&schema=${schema}&table=all_recents&order_by=created_at`;
  }

  // Canada, Mexico, UK use the /schema endpoint
  return `${baseUrl}/posts/recent/schema?limit=${feedLength}&skip=0&schema=${schema}`;
}, [countryCode, feedLength, getApiSchema]);

  // Cache key generator
  const getCacheKey = useCallback((url) => {
    return `recents_${url}_${Date.now() - (Date.now() % CACHE_DURATION)}`;
  }, []);

  // Check if parameters changed
  const isSameParams = useCallback((newParams) => {
    if (!lastFetchParams.current) return false;
    return JSON.stringify(lastFetchParams.current) === JSON.stringify(newParams);
  }, []);

  // Client-side search filter over the fetched recents data
  const filterDataBySearch = useCallback((data, searchTerm) => {
    if (!searchTerm || !data) return data;

    return data.filter(item => {
      const title = item.title || item.status_title || '';
      const description = item.description || '';
      const source = item.collection_name || item.source || '';

      const searchLower = searchTerm.toLowerCase();

      return (
        title.toLowerCase().includes(searchLower) ||
        description.toLowerCase().includes(searchLower) ||
        source.toLowerCase().includes(searchLower)
      );
    });
  }, []);

  useEffect(() => {
    const fetchRecentsData = async () => {
      if (!countryCode) return;

      const currentParams = {
        countryCode,
        feedLength,
        searchPerformed,
        searchQuery
      };

      // Skip if params haven't changed
      if (isSameParams(currentParams)) {
        return;
      }

      // Cancel previous request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      abortControllerRef.current = new AbortController();
      lastFetchParams.current = currentParams;

      setLoading(true);
      setError(null);

      try {
        const url = buildRecentsApiUrl();
        const cacheKey = getCacheKey(url);

        // Check cache first
        const cachedResult = recentsCache.get(cacheKey);
        if (cachedResult) {
          // Defensively re-filter on read in case anything future-dated slipped in.
          let finalData = dropFutureDated(cachedResult);

          // Apply client-side search over the fetched data.
          if (searchPerformed && searchQuery.trim()) {
            finalData = filterDataBySearch(finalData, searchQuery.trim());
          }

          setData(finalData);
          setLoading(false);
          return;
        }

        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${apiKey}`,
          },
          signal: abortControllerRef.current.signal,
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();

        // Drop any future-dated items before caching so the cache stays clean
        // and stale future-dated records can't slip back in until expiry.
        const cleanedResult = dropFutureDated(result);

        // Cache the cleaned, UNFILTERED result so the same fetch can serve any
        // search term without re-hitting the network.
        recentsCache.set(cacheKey, cleanedResult);

        // Clean up old cache entries
        if (recentsCache.size > 50) {
          const oldEntries = Array.from(recentsCache.keys()).slice(0, 25);
          oldEntries.forEach(key => recentsCache.delete(key));
        }

        let finalData = cleanedResult;

        // Apply client-side search over the fetched data.
        if (searchPerformed && searchQuery.trim()) {
          finalData = filterDataBySearch(cleanedResult, searchQuery.trim());
        }

        setData(finalData);

      } catch (err) {
        if (err.name === 'AbortError') {
          return;
        }

        setError(err.message);
        console.error('Error fetching recents data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchRecentsData();

    // Cleanup on unmount
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [buildRecentsApiUrl, getCacheKey, isSameParams, countryCode, feedLength, searchPerformed, searchQuery, filterDataBySearch]);

  // Return additional metadata for recents
  return {
    data,
    loading,
    error,
    isRecentsData: true,
    countryCode,
    totalItems: data?.length || 0
  };
};

// Export country schema mapping for use elsewhere
export { COUNTRY_SCHEMA_MAPPING };